import type { ModuleConfig } from '../types/config';
import type { DashboardQuickAction } from '../services/api/types';
import { moduleCodeForScreen } from './moduleAccess';

const MODULE_CODE_CANONICAL: Record<string, string> = {
  reportcards: 'report_card',
  report_cards: 'report_card',
  grades: 'exams',
  assignment: 'assignments',
  fee: 'fees',
  certificate: 'certificate',
};

/** Normalize sidebar paths so `/foo` and `/foo/` dedupe together. */
export function normalizeNavPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed || trimmed === '/') return '/';
  return trimmed.replace(/\/+$/, '');
}

export function canonicalModuleCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const normalized = code.trim().toLowerCase();
  if (!normalized) return null;
  return MODULE_CODE_CANONICAL[normalized] ?? normalized;
}

/** Drop duplicate catalog rows that share a route or backend module code. */
export function dedupeNavigationModules(modules: ModuleConfig[]): ModuleConfig[] {
  const seenPaths = new Set<string>();
  const seenCodes = new Set<string>();
  const deduped: ModuleConfig[] = [];

  for (const module of modules) {
    const path = normalizeNavPath(module.path);
    let code = canonicalModuleCode(moduleCodeForScreen(module) ?? module.id);
    if (code === 'teachers' || code === 'teacher') code = 'staff';
    if (seenPaths.has(path)) continue;
    if (code && seenCodes.has(code)) continue;
    seenPaths.add(path);
    if (code) seenCodes.add(code);
    deduped.push(module);
  }

  return deduped;
}

/** Paths that have a dedicated sidebar item and should not repeat in dashboard quick actions. */
const REDUNDANT_QUICK_ACTION_PATHS = new Set(['/report-cards', '/reportcards', '/grades']);

function isReportCardQuickAction(action: DashboardQuickAction): boolean {
  const label = String(action.label ?? '').trim().toLowerCase();
  const to = normalizeNavPath(action.to ?? '');
  if (REDUNDANT_QUICK_ACTION_PATHS.has(to)) return true;
  return /\breport\s*cards?\b/.test(label);
}

/** Drop duplicate quick-action targets and legacy report-card shortcuts. */
export function dedupeDashboardQuickActions(
  actions: DashboardQuickAction[],
  _options?: { variant?: 'admin' | 'teacher' }
): DashboardQuickAction[] {
  const seen = new Set<string>();

  return actions.filter((action) => {
    if (isReportCardQuickAction(action)) return false;
    const to = normalizeNavPath(action.to ?? '');
    if (!to || seen.has(to)) return false;
    seen.add(to);
    return true;
  });
}
